<script setup>
import { useNotifier } from '@daysmart/daysmart-vue-components/composables'
import mod from '@/core/utils/url/mod'

const props = defineProps({
  eligibility: {
    type: Object,
  },
  event: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'close', 'redirectToLogin'])
const api = useApi()
const { d, t } = useI18n()
const { r } = useRoutes()
const notify = useNotifier()
const selectedCustomer = useSelectedCustomer()
const dayjs = useDayjs()

const isAppointment = toRef(() => props.event?.type === 'appointment')
const isPastLateCancel = computed(() => {
  const registrationTimeline = props.event?.active_registration_status

  if (!registrationTimeline)
    return false

  const lateCancel = new Date(registrationTimeline.late_cancels_after)
  const now = new Date()

  return lateCancel <= now
})

const showAfterCancellation = ref(false)
const selectedMembershipOption = ref(null)
const apiResult = ref(null)
const validPeriodTypes = ['day', 'week', 'month', 'year', 'billing_cycle', 'lifetime']
const sortedResult = computed(() => {
  return apiResult.value
    ? apiResult.value.usage_periods.sort((a, b) => validPeriodTypes.indexOf(a.period_type) - validPeriodTypes.indexOf(b.period_type))
    : []
})

const selectedMembershipUsages = computed(() => {
  if (!apiResult.value)
    return ''

  const usages = []
  const result = sortedResult.value

  for (const period of result) {
    const periodType = period.period_type
    const remainingCount = period.remaining_count

    if (validPeriodTypes.includes(periodType)) {
      if (periodType === 'lifetime')
        usages.push(t('1 use left in the pack | {count} uses left in the pack', remainingCount, { count: remainingCount }))

      else if (periodType === 'billing_cycle')
        usages.push(t('1 use left this billing cycle | {count} uses left this billing cycle', remainingCount, { count: remainingCount }))

      else if (remainingCount > 0)
        usages.push(t(`1 use left this ${periodType} | {count} uses left this ${periodType}`, remainingCount, { count: remainingCount }))
    }
  }

  return usages.join(', ')
})
const isEventFree = toRef(() => props.eligibility?.drop_in_price?.decimal === 0)
const isReservationAllowed = toRef(() => props.eligibility?.recurring_reservations?.allowed)

const unrestrictedMembershipOptions = computed(() => {
  const unrestrictedOptions = props.eligibility?.membership_options.filter(option => !option.restrictions.length) || []
  const hasSingleSessionOption = props.eligibility?.drop_in_price

  const singleSessionOption = hasSingleSessionOption
    ? [{
        name: t('Single Session'),
        description: t('Pay for this single registration.'),
        price: props.eligibility?.drop_in_price,
        isSingleSession: true,
      }]
    : []
  return [
    ...singleSessionOption,
    ...unrestrictedOptions,
  ].map(option => ({ ...option, label: option.name, value: option }))
})
const membershipPenalties = ref()

watch(
  unrestrictedMembershipOptions,
  () => {
    if (unrestrictedMembershipOptions.value.length === 1) {
      selectedMembershipOption.value = unrestrictedMembershipOptions.value[0]
    }
    else if (unrestrictedMembershipOptions.value.length === 2 && unrestrictedMembershipOptions.value.some(option => option.isSingleSession)) {
      const nonDropInMemberships = unrestrictedMembershipOptions.value.filter(option => !option.isSingleSession)
      if (nonDropInMemberships.length === 1)
        selectedMembershipOption.value = nonDropInMemberships[0]
    }
  },
  { immediate: true },
)

async function fetchMembershipUses() {
  if (!selectedMembershipOption.value)
    return
  const eventDate = dayjs.value(props.event?.starts_at).format('YYYY-MM-DD')
  const result = await api.getUsesFromCustomerMembershipId({
    customerMembershipId: selectedMembershipOption.value.customer_membership,
    queryParameters: {
      date: eventDate,
    },
  })

  apiResult.value = result
}

async function bookNow(membershipId) {
  if (isPastLateCancel.value) {
    const result = await api.retrieveCustomerMembership(selectedMembershipOption.value?.customer_membership, {
      expand: ['membership.penalty_system'],
    })
    if (result)
      membershipPenalties.value = result.membership.penalty_system
    showAfterCancellation.value = true
  }

  else {
    completeBookingProcess(membershipId)
  }
}

async function completeBookingProcess(customerMembershipIdForPurchase) {
  // Initiate_Purchase: Allotment Fee + Drop In
  // Register: No Fee, Free Class
  let result

  if (isEventFree.value || customerMembershipIdForPurchase === null) {
    if (isAppointment.value) {
      result = await api.registerForAppointment(props.event.id, {
        data: {
          customer: selectedCustomer.value,
          ...(selectedMembershipOption.value?.customer_membership && {
            customer_membership:
            selectedMembershipOption.value?.customer_membership,
          }),
        },
      })
    }
    else {
      result = await api.registerForEvent(props.event.id, {
        data: {
          customer: selectedCustomer.value,
          ...(selectedMembershipOption.value?.customer_membership && {
            customer_membership:
            selectedMembershipOption.value?.customer_membership,
          }),
        },
      })
    }

    notify.emit({
      type: 'success',
      title: t('Success'),
      content: t('Booked Successfully'),
    })
  }
  else {
    if (isAppointment.value) {
      result = await api.initiateAppointmentSlotPurchase(props.event?.id, {
        data: {
          customer: selectedCustomer.value,
          return_to: mod({
            url: window.location.href,
            excludeParams: ['auth0IdToken'],
          }),
        },
      })
    }
    else {
      result = await api.initiatePurchase(props.event?.id, {
        data: {
          customer: selectedCustomer.value,
          return_to: mod({
            url: window.location.href,
            excludeParams: ['auth0IdToken'],
          }),
          ...(customerMembershipIdForPurchase !== 'drop_in' ? { customer_membership: customerMembershipIdForPurchase } : { select_drop_in: true }), // for allotment price or drop-in
        },
      })
    }
    if (result.next_url) {
      window.location.href = r({
        path: result.next_url,
      })
    }
  }

  if (isAppointment.value && result.event)
    emit('update', { registeredEventId: result.event })
  else
    emit('update')
}

watch(
  selectedMembershipOption,
  () => {
    if (selectedMembershipOption.value && !selectedMembershipOption.value.is_drop_in_membership)
      fetchMembershipUses()
  },
)
</script>

<template>
  <!-- After Cancellation -->
  <template v-if="showAfterCancellation">
    <div class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap">
      <div class="flex flex-col flex-gap-y-2">
        <p class="text-text fw-600">
          {{ t('Are you sure you want to book') }}?
        </p>
        <p class="text-text">
          {{ t('It is after the cancellation deadline. You will not receive a refund if you leave the class after booking') }}.
        </p>
        <p
          v-if="!!membershipPenalties && membershipPenalties.late_cancel_rule?.max_infractions"
          class="text-text"
        >
          {{ t('late_cancel_rule', { late_cancel: t('late_cancel', membershipPenalties.late_cancel_rule?.max_infractions), days: t('days', membershipPenalties.late_cancel_rule?.days), price: membershipPenalties.late_cancel_rule?.price?.string }) }}
        </p>
      </div>
      <div class="flex flex-col flex-gap-y-2">
        <DSAsyncButton
          class="btn btn-md w-full uppercase btn-fill-primary"
          @submit="completeBookingProcess(selectedMembershipOption?.on_allotment_price?.decimal > 0
            ? selectedMembershipOption?.customer_membership
            : (selectedMembershipOption?.isSingleSession ? 'drop_in' : null),
          )"
        >
          {{ selectedMembershipOption?.on_allotment_price?.decimal > 0 || selectedMembershipOption?.isSingleSession ? t('Purchase') : t('Book Now') }}
        </DSAsyncButton>
        <DSButton
          class="btn-md w-full uppercase btn-outline-secondary"
          @click="emit('close')"
        >
          {{ t('Cancel') }}
        </DSButton>
      </div>
    </div>
  </template>
  <!-- Class is free alert -->
  <template v-else-if="isEventFree">
    <div class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap">
      <DSAlert
        type="info"
        no-close
        class="w-100%"
      >
        {{ t('Class is Free') }}.
      </DSAlert>
      <DSAsyncButton
        class="btn btn-md w-full uppercase btn-fill-primary"
        @submit="bookNow()"
      >
        {{ t('Book Now') }}
      </DSAsyncButton>
    </div>
  </template>
  <!--  Select Family Member + Registration Dropdown -->
  <template v-else>
    <div class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap">
      <div class="registration-dropdown">
        <label
          for="registration-options-dropdown"
          class="mb-1 text-text-600 font-600"
        >
          {{ t('Registration Options') }}
        </label>
        <DSListboxInput
          v-model="selectedMembershipOption"
          :options="unrestrictedMembershipOptions"
        >
          <template #inputLabel>
            <span class="flex flex-row flex-gap-2 text-left text-sm text-text-500 fw-400">
              <template v-if="selectedMembershipOption">
                {{ selectedMembershipOption.name }}
                <DSChip
                  v-if="selectedMembershipOption?.isSingleSession | selectedMembershipOption.on_allotment_price?.decimal !== 0"
                  type="warning"
                >
                  +{{ selectedMembershipOption.price?.string || selectedMembershipOption.on_allotment_price?.string }}
                </DSChip>
              </template>
              <template v-else>
                {{ t(
                  '{n} Valid Memberships',
                  unrestrictedMembershipOptions?.length,
                  { n: unrestrictedMembershipOptions?.length },
                ) }}
              </template>
            </span>
          </template>
          <template #option="{ option }">
            <div
              class="flex flex-col cursor-pointer b-b-1 px-4 py-2 text-left text-sm text-text-500 last:b-b-0 hover:bg-surface-300"
            >
              <div class="mb-1 flex justify-between">
                <p class="text-text-500">
                  {{ option.label }}
                </p>
                <DSChip
                  v-if="option?.isSingleSession || option.on_allotment_price.decimal !== 0"
                  type="warning"
                >
                  +{{ option?.price?.string || option.on_allotment_price.string }}
                </DSChip>
              </div>
              <p
                v-if="!option?.isSingleSession"
                class="text-xs text-text-400"
              >
                {{ d(option.start_date, 'medium') }} -
                {{
                  option.expiration_date
                    ? d(option.expiration_date, 'medium')
                    : ''
                }}
              </p>
            </div>
          </template>
        </DSListboxInput>
        <p
          v-if="!selectedMembershipOption?.is_drop_in_membership"
          class="mt-0.5 break-words text-xs text-text-400"
        >
          {{ selectedMembershipUsages }}
        </p>
      </div>
      <div class="flex flex-col flex-gap-y-2">
        <DSAsyncButton
          class="btn btn-md w-full truncate uppercase btn-fill-primary"
          :disabled="!selectedMembershipOption"
          @submit="bookNow(
            selectedMembershipOption?.on_allotment_price?.decimal > 0
              ? selectedMembershipOption?.customer_membership
              : (selectedMembershipOption?.isSingleSession ? 'drop_in' : null),
          )"
        >
          {{ selectedMembershipOption?.on_allotment_price?.decimal > 0 || selectedMembershipOption?.isSingleSession ? t('Purchase') : t('Book Now') }}
        </DSAsyncButton>
        <BookingModalNewReservationButton
          v-if="isReservationAllowed"
          :eligibility="eligibility"
          :event="event"
        />
      </div>
    </div>
  </template>
</template>

<i18n>
{
  "en-us": {
    "Class is Free": "Class is Free",
    "Are you sure you want to book": "Are you sure you want to book",
    "It is after the cancellation deadline. You will not receive a refund if you leave the class after booking": "It is after the cancellation deadline. You will not receive a refund if you leave the class after booking",
    "Registration Options": "Registration Options",
    "{n} Valid Memberships": "{n} Valid Memberships",
    "1 use left this day | {count} uses left this day": "1 use left this day | {count} uses left this day",
    "1 use left this week | {count} uses left this week": "1 use left this week | {count} uses left this week",
    "1 use left this month | {count} uses left this month": "1 use left this month | {count} uses left this month",
    "1 use left this year | {count} uses left this year": "1 use left this year | {count} uses left this year",
    "1 use left in the pack | {count} uses left in the pack": "1 use left in the pack | {count} uses left in the pack",
    "1 use left this billing cycle | {count} uses left this billing cycle": "1 use left this billing cycle | {count} uses left this billing cycle",
    "Purchase": "Purchase",
    "Book Now": "Book Now",
    "Cancel": "Cancel",
    "Success": "Success",
    "Booked Successfully": "Booked Successfully",
    "1 use left in this billing cycle | {count} uses left in this billing cycle": "1 use left in this billing cycle | {count} uses left in this billing cycle",
    "Single Session": "Single Session",
    "Pay for this single registration.": "Pay for this single registration",
    "late_cancel": "1 time | {n} times",
    "days": "1 day | {n} days",
    "late_cancel_rule": "If you Late Cancel {late_cancel} within {days} you will be subject to a fee of {price}"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Class is Free": "Kurs ist frei",
  "Are you sure you want to book": "Bist du sicher, dass du buchen möchtest",
  "It is after the cancellation deadline. You will not receive a refund if you leave the class after booking": "Die Stornierungsfrist ist bereits abgelaufen. Du erhältst keine Rückerstattung, wenn du den Kurs nach der Registrierung verlässt.",
  "Registration Options": "Registrierungsoptionen",
  "{n} Valid Memberships": "{n} gültige Mitgliedschaften",
  "1 use left this day | {count} uses left this day": "Noch 1 Nutzung an diesem Tag | Noch {count} Nutzungen an diesem Tag",
  "1 use left this week | {count} uses left this week": "Noch 1 Nutzung in dieser Woche | Noch {count} Nutzungen in dieser Woche",
  "1 use left this month | {count} uses left this month": "Noch 1 Nutzung in diesem Monat | Noch {count} Nutzungen in diesem Monat",
  "1 use left this year | {count} uses left this year": "Noch 1 Nutzung in diesem Jahr | Noch {count} Nutzungen in diesem Jahr",
  "1 use left in the pack | {count} uses left in the pack": "Noch 1 Nutzung in diesem Jahr | Noch {count} Nutzungen in diesem Jahr",
  "Purchase": "Kaufen",
  "Book Now": "Jetzt buchen",
  "Cancel": "Abbrechen",
  "Success": "Erfolg",
  "Booked Successfully": "Erfolg",
  "1 use left this billing cycle | {count} uses left this billing cycle": "Noch 1 Nutzung in diesem Abrechnungszyklus übrig | Noch {count} Nutzungen in diesem Abrechnungszyklus übrig",
  "1 use left in this billing cycle | {count} uses left in this billing cycle": "Noch 1 Nutzung in diesem Abrechnungszyklus übrig | Noch {count} Nutzungen in diesem Abrechnungszyklus übrig",
  "Single Session": "Einzelsitzung",
  "Pay for this single registration.": "Für diese Einzelregistrierung bezahlen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Class is Free": "Le cours est gratuit",
  "Are you sure you want to book": "Voulez-vous vraiment réserver",
  "It is after the cancellation deadline. You will not receive a refund if you leave the class after booking": "Le délai d'annulation prévu est dépassé. Vous ne recevrez aucun remboursement si vous quittez le cours après l'inscription",
  "Registration Options": "Options d'inscription",
  "{n} Valid Memberships": "{n} abonnements valides",
  "1 use left this day | {count} uses left this day": "1 utilisation restante ce jour | {count} utilisations restantes ce jour",
  "1 use left this week | {count} uses left this week": "1 utilisation restante cette semaine | {count} utilisations restantes cette semaine",
  "1 use left this month | {count} uses left this month": "1 utilisation restante ce mois | {count} utilisations restantes ce mois",
  "1 use left this year | {count} uses left this year": "1 utilisation restante cette année | {count} utilisations restantes cette année",
  "1 use left in the pack | {count} uses left in the pack": "1 utilisation restante dans le pack | {count} utilisations restantes dans le pack",
  "Purchase": "Acheter",
  "Book Now": "Réserver maintenant",
  "Cancel": "Annuler",
  "Success": "Succès",
  "Booked Successfully": "Réservé avec succès",
  "1 use left this billing cycle | {count} uses left this billing cycle": "1 utilisation restante pour de ce cycle de facturation | {count} utilisations restantes pour de ce cycle de facturation",
  "1 use left in this billing cycle | {count} uses left in this billing cycle": "1 utilisation restante pour de ce cycle de facturation | {count} utilisations restantes pour de ce cycle de facturation",
  "Single Session": "Séance unique",
  "Pay for this single registration.": "Payer pour cette inscription simple."
}
</i18n>

<i18n locale="es" lang="json">
{
  "Class is Free": "La clase es gratuita",
  "Are you sure you want to book": "¿Seguro que quiere reservar?",
  "It is after the cancellation deadline. You will not receive a refund if you leave the class after booking": "La fecha límite de cancelación ya se ha superado. Si abandona la clase tras haberla reservado, no recibirá ningún reembolso",
  "Registration Options": "Opciones de inscripción",
  "{n} Valid Memberships": "{n} suscripciones válidas",
  "1 use left this day | {count} uses left this day": "Queda 1 uso para este día | Quedan {count} usos para este día",
  "1 use left this week | {count} uses left this week": "Queda 1 uso para esta semana | Quedan {count} usos para esta semana",
  "1 use left this month | {count} uses left this month": "Queda 1 uso para este mes | Quedan {count} usos para este mes",
  "1 use left this year | {count} uses left this year": "Queda 1 uso para este año | Quedan {count} usos para este año",
  "1 use left in the pack | {count} uses left in the pack": "Queda 1 uso para este paquete | Quedan {count} usos para este paquete",
  "Purchase": "Comprar",
  "Book Now": "Reservar ahora",
  "Cancel": "Cancelar",
  "Success": "Correcto",
  "Booked Successfully": "Reservado correctamente",
  "1 use left this billing cycle | {count} uses left this billing cycle": "Queda 1 uso para este ciclo de facturación | Quedan {count} usos para este ciclo de facturación",
  "1 use left in this billing cycle | {count} uses left in this billing cycle": "Queda 1 uso para este ciclo de facturación | Quedan {count} usos para este ciclo de facturación",
  "Single Session": "Sesión única",
  "Pay for this single registration.": "Pagar por este registro único"
}
</i18n>
